import IChartService from '../IChartService'
import {
  BarChartOptions,
  ChartDataProperties,
  ChartOptionsType,
  PieChartOptions,
  QueryResultTableOptions,
} from '@/models/chart.model'
import {
  ChartDetails,
  ChartDetailsRow,
  GroupDisplayConfig,
  WidgetProperties,
} from '@/models/dashboard.model'
import { assertUnreachable } from '@/utils/helpers'
import {
  getDefaultConfiguration,
  resolveGroupDisplay,
} from '@/composables/useQueryResultAggregation'

export default class ChartService implements IChartService {
  constructor() {}

  computeChartOptions(widget: WidgetProperties): ChartOptionsType {
    if (!widget.rawQueryData) {
      throw new Error('Cannot create chart without data')
    }
    return this.computeQueryResultChartOptions(widget)
  }

  protected computeQueryResultChartOptions(widget: WidgetProperties): ChartOptionsType {
    const rawData = widget.rawQueryData!

    if (widget.chartType === 'table') {
      return this.computeQueryResultTableOptions(widget, rawData)
    }

    // Use backend-aggregated data if available, otherwise fallback to frontend aggregation
    const displayData = this.getAggregatedChartData(widget, rawData)

    switch (widget.chartType) {
      case 'pie':
        return this.computeQueryResultPieOptions(widget, displayData)
      case 'bar':
        return this.computeQueryResultBarOptions(widget, displayData)
      default:
        return assertUnreachable(widget.chartType)
    }
  }

  protected sortRowsByDisplayOrder(
    rows: ChartDetailsRow[],
    displayConfigs?: GroupDisplayConfig[],
  ): ChartDetailsRow[] {
    if (!displayConfigs || displayConfigs.length === 0) return rows
    const orderMap = new Map(displayConfigs.map((dc, i) => [dc.originalName, i]))
    return [...rows].sort((a, b) => {
      const nameA = a.cells[0]?.value ?? ''
      const nameB = b.cells[0]?.value ?? ''
      const orderA = orderMap.get(nameA) ?? Number.MAX_SAFE_INTEGER
      const orderB = orderMap.get(nameB) ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    })
  }

  protected getAggregatedChartData(
    widget: WidgetProperties,
    rawData: ChartDetails,
  ): ChartDataProperties[] {
    const aggregatedData = widget.aggregatedQueryData
    const config = widget.configuration.queryResult ?? getDefaultConfiguration(rawData)
    const displayConfigs = config.groupDisplayConfigs

    if (!aggregatedData) return []

    const hiddenSeries = widget.hiddenSeries ?? []
    return this.sortRowsByDisplayOrder(aggregatedData.rows, displayConfigs)
      .filter((row) => !hiddenSeries.includes(row.cells[0]?.value ?? ''))
      .map((row) => {
        const originalName = row.cells[0]?.value ?? ''
        const count = row.cells[1] ? Number(row.cells[1].value ?? 0) : 0
        const resolved = resolveGroupDisplay(originalName, displayConfigs)
        return {
          name: resolved.displayName,
          count,
          color: resolved.color ?? '#888888',
        }
      })
  }

  protected computeQueryResultTableOptions(
    widget: WidgetProperties,
    rawData: ChartDetails,
  ): QueryResultTableOptions {
    const aggregatedData = widget.aggregatedQueryData

    if (aggregatedData) {
      const aggColors = widget.configuration.queryResult?.aggregationColors
      const displayConfigs = widget.configuration.queryResult?.groupDisplayConfigs
      const headers = aggregatedData.columns.map((col, i) => ({
        title: col.titleDefault || col.id,
        key: `col_${i}`,
        sortable: true,
        color: i > 0 ? aggColors?.[col.id] : undefined,
      }))
      const links: Record<number, Record<string, string>> = {}
      const items = this.sortRowsByDisplayOrder(aggregatedData.rows, displayConfigs).map(
        (row, r) => {
          const item: Record<string, string | number> = {}
          row.cells.forEach((cell, i) => {
            const columnKey = `col_${i}`
            if (cell.artifactId > 0) {
              if (!links[r]) {
                links[r] = {}
              }
              links[r][columnKey] = cell.artifactId.toString()
            }

            if (i === 0) {
              const resolved = resolveGroupDisplay(cell.value, displayConfigs, false)
              item[columnKey] = resolved.displayName
              item['_groupKey'] = resolved.originalName
            } else {
              item[columnKey] = cell.value
            }
          })
          return item
        },
      )
      const rowColors: Record<string, string> = {}
      if (displayConfigs) {
        for (const dc of displayConfigs) {
          if (dc.color) {
            rowColors[dc.originalName] = dc.color
          }
        }
      }
      return {
        headers,
        items,
        links,
        hiddenSeries: widget.hiddenSeries ?? [],
        rowColors: Object.keys(rowColors).length > 0 ? rowColors : undefined,
        type: 'queryResultTable' as const,
      }
    }

    const headers = rawData.columns.map((col) => ({
      title: col.titleDefault || col.id,
      key: col.id.replace(/\./g, '_').toLowerCase(),
      sortable: true,
    }))

    const links: Record<number, Record<string, string>> = {}
    const items = rawData.rows.map((row, r) => {
      const item: Record<string, string | number> = {}
      row.cells.forEach((cell, index) => {
        if (rawData.columns[index]) {
          const columnKey = rawData.columns[index].id.replace(/\./g, '_').toLowerCase()
          item[columnKey] = cell.value
          if (cell.artifactId > 0) {
            if (!links[r]) {
              links[r] = {}
            }
            links[r][columnKey] = cell.artifactId.toString()
          }
        }
      })
      return item
    })

    return {
      headers,
      items,
      links,
      hiddenSeries: [],
      type: 'queryResultTable',
    }
  }

  protected computeQueryResultPieOptions(
    widget: WidgetProperties,
    data: ChartDataProperties[],
  ): PieChartOptions {
    return {
      charttitle: widget.title,
      legend: {
        data: data.map((item) => item.name),
        show: false,
        selected: this.legendSelected(widget.hiddenSeries),
      },
      tooltip: {
        formatter: '{b} : {c} ({d}%)',
        trigger: 'item',
      },
      color: data.map((item) => item.color),
      series: [
        {
          name: widget.title,
          type: 'pie',
          data: data
            .filter((item) => item.count !== 0)
            .map((item) => ({
              name: item.name,
              value: item.count,
            })),
          label: {
            show: widget.configuration?.showLabels ?? true,
          },
        },
      ],
      type: 'pie',
    }
  }

  protected computeQueryResultBarOptions(
    widget: WidgetProperties,
    data: ChartDataProperties[],
  ): BarChartOptions {
    const categories = data.map((item) => item.name)
    const rotateLabels = categories.length > 6
    const maxLabelWidth = 240
    const barData = data.map((item) => ({
      name: item.name,
      value: item.count,
      itemStyle: { color: item.color },
    }))

    const dynamicLeft = rotateLabels
      ? this.estimateRotatedLabelOverflow(categories, maxLabelWidth)
      : 0

    return {
      charttitle: widget.title,
      legend: { data: [], show: false, selected: {} },
      tooltip: {
        formatter: '{b} : {c}',
        trigger: 'item',
      },
      yAxis: { type: 'value' },
      xAxis: {
        data: categories,
        type: 'category',
        axisLabel: {
          interval: 0,
          rotate: rotateLabels ? 45 : 0,
          ...(rotateLabels && { overflow: 'truncate' as const, width: maxLabelWidth }),
        },
      },
      grid: {
        left: dynamicLeft,
        right: 0,
        bottom: 0,
        containLabel: true,
      },
      series: [
        {
          type: 'bar',
          name: widget.title,
          label: {
            show: widget.configuration?.showLabels ?? true,
            formatter: '{c}',
            color: '#000',
            position: 'top',
            rotate: 0,
            verticalAlign: 'bottom',
          },
          data: barData,
        },
      ],
      type: 'bar',
    }
  }

  protected estimateRotatedLabelOverflow(labels: string[], maxWidth: number): number {
    const charWidth = 6
    const longestLabel = labels.reduce((a, b) => (a.length > b.length ? a : b), '')
    const labelPx = Math.min(longestLabel.length * charWidth, maxWidth)
    // containLabel handles most of the space; we only add extra for the first label's overhang
    return Math.round(labelPx * Math.sin(Math.PI / 4) * 0.55)
  }

  protected legendSelected(hiddenSeries: string[]): Record<string, boolean> {
    return hiddenSeries.reduce((acc: Record<string, boolean>, curr) => {
      acc[curr] = false
      return acc
    }, {})
  }
}
